import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Building2, Users, DollarSign, Globe, Phone, MapPin, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { apiClient } from '../lib/apiClient';
import { usePlanInfo } from '../hooks/usePlanInfo';
import EnforcementModal from './EnforcementModal';

interface HubSpotCompany {
  id: string;
  name: string;
  website: string;
  industry: string;
  annual_revenue: number;
  employee_count: number;
  phone: string;
  city: string;
  state: string;
  country: string;
  description: string;
  last_modified: string;
  created_date: string;
  is_sample_data: boolean;
}

interface HubSpotCompanyListData {
  companies: HubSpotCompany[];
  summary: {
    total_companies: number;
    has_more: boolean;
  };
  pagination: {
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

interface HubSpotCompanyImportModalProps {
  onClose: () => void;
}

const HubSpotCompanyImportModal: React.FC<HubSpotCompanyImportModalProps> = ({ onClose }) => {
  const queryClient = useQueryClient();
  const { data: planInfo } = usePlanInfo();
  const [selectedCompanies, setSelectedCompanies] = useState<Set<string>>(new Set());
  const [showEnforcementModal, setShowEnforcementModal] = useState(false);
  const [enforcementError, setEnforcementError] = useState<any>(null);

  // Fetch HubSpot companies
  const { data: companyData, isLoading, error, refetch } = useQuery<HubSpotCompanyListData>({
    queryKey: ['hubspot-companies'],
    queryFn: async () => {
      return apiClient.customRequest('/api/integrations/hubspot/companies');
    },
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true
  });

  // Import selected companies mutation
  const importMutation = useMutation<any, Error, string[]>({
    mutationFn: async (companyIds: string[]) => {
      return apiClient.customRequest('/api/integrations/hubspot/import-companies', {
        method: 'POST',
        body: { company_ids: companyIds }
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      onClose();
      // Show success message
      alert(`Successfully imported ${data.companies_imported} of ${data.companies_requested} selected companies!`);
    },
    onError: (error: any) => {
      console.error('Import error:', error);
      
      // Check if it's a plan limit error
      if (error.response?.status === 403) {
        const errorData = error.response.data;
        const enforcementError = errorData.error || errorData;
        
        if (enforcementError.error === 'plan_limit_exceeded' || 
            enforcementError.message?.includes('Limit of')) {
          setEnforcementError(enforcementError);
          setShowEnforcementModal(true);
          return;
        }
      }
      
      alert(`Failed to import companies: ${error.message}`);
    }
  });

  const toggleCompany = (companyId: string) => {
    const newSelected = new Set(selectedCompanies);
    if (newSelected.has(companyId)) {
      newSelected.delete(companyId);
    } else {
      newSelected.add(companyId);
    }
    setSelectedCompanies(newSelected);
  };

  const selectAllCompanies = () => {
    if (!companyData) return;
    const allCompanyIds = companyData.companies.map(company => company.id);
    setSelectedCompanies(new Set(allCompanyIds));
  };

  const selectNone = () => {
    setSelectedCompanies(new Set());
  };

  const handleImport = async () => {
    if (selectedCompanies.size === 0) {
      alert('Please select at least one company to import.');
      return;
    }

    const confirmMessage = `Are you sure you want to import ${selectedCompanies.size} selected companies?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    importMutation.mutate(Array.from(selectedCompanies));
  };

  const formatCurrency = (amount: number) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getEmployeeCountBadge = (count: number) => {
    if (!count) return 'Unknown';
    if (count < 50) return 'Small (1-49)';
    if (count < 250) return 'Medium (50-249)';
    if (count < 1000) return 'Large (250-999)';
    return 'Enterprise (1000+)';
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <Card className="w-full max-w-2xl shadow-2xl border-0 bg-white/95 backdrop-blur-md">
          <CardHeader className="text-center">
            <CardTitle className="text-xl text-gray-900">Loading HubSpot Companies</CardTitle>
          </CardHeader>
          <CardContent className="flex justify-center py-8">
            <LoadingSpinner size="lg" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <Card className="w-full max-w-2xl shadow-2xl border-0 bg-white/95 backdrop-blur-md">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl text-gray-900 flex items-center">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                Error Loading Companies
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="hover:bg-gray-100 rounded-full p-2"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              Failed to load HubSpot companies: {error.message}
            </p>
            <div className="flex gap-2">
              <Button onClick={() => refetch()} variant="outline">
                Retry
              </Button>
              <Button onClick={onClose}>
                Close
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const companies = companyData?.companies || [];

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <Card className="w-full max-w-6xl max-h-[90vh] overflow-hidden shadow-2xl border-0 bg-white/95 dark:bg-gray-800/95 backdrop-blur-md">
          {/* Header */}
          <CardHeader className="border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg">
                  <Building2 className="h-6 w-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-xl text-gray-900 dark:text-gray-100">
                    Import HubSpot Companies
                  </CardTitle>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Select companies to import as accounts ({companies.length} available)
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full p-2"
                disabled={importMutation.isPending}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>

          <CardContent className="p-0 flex-1 overflow-hidden">
            {/* Selection Controls */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={selectAllCompanies}
                    disabled={companies.length === 0}
                  >
                    Select All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={selectNone}
                    disabled={selectedCompanies.size === 0}
                  >
                    Select None
                  </Button>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedCompanies.size} of {companies.length} companies selected
                </div>
              </div>
            </div>

            {/* Companies List */}
            <div className="flex-1 overflow-y-auto max-h-96">
              {companies.length === 0 ? (
                <div className="p-8 text-center">
                  <Building2 className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">No companies found in your HubSpot account.</p>
                </div>
              ) : (
                <div className="p-4 space-y-3">
                  {companies.map((company) => (
                    <div
                      key={company.id}
                      className={`p-4 border rounded-lg transition-all duration-200 cursor-pointer hover:shadow-md ${
                        selectedCompanies.has(company.id)
                          ? 'border-orange-300 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-400 shadow-sm'
                          : 'border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                      onClick={() => toggleCompany(company.id)}
                    >
                      <div className="flex items-start space-x-4">
                        {/* Checkbox */}
                        <div className="flex-shrink-0 mt-1">
                          <input
                            type="checkbox"
                            checked={selectedCompanies.has(company.id)}
                            onChange={() => toggleCompany(company.id)}
                            className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                          />
                        </div>

                        {/* Company Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                                {company.name}
                              </h4>
                              
                              {/* Company Details */}
                              <div className="mt-2 grid grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                                {company.industry && (
                                  <div className="flex items-center text-gray-600 dark:text-gray-400">
                                    <Building2 className="h-4 w-4 mr-1 flex-shrink-0" />
                                    <span className="truncate">{company.industry}</span>
                                  </div>
                                )}
                                
                                {company.employee_count > 0 && (
                                  <div className="flex items-center text-gray-600 dark:text-gray-400">
                                    <Users className="h-4 w-4 mr-1 flex-shrink-0" />
                                    <span>{getEmployeeCountBadge(company.employee_count)}</span>
                                  </div>
                                )}
                                
                                {company.annual_revenue > 0 && (
                                  <div className="flex items-center text-gray-600 dark:text-gray-400">
                                    <DollarSign className="h-4 w-4 mr-1 flex-shrink-0" />
                                    <span>{formatCurrency(company.annual_revenue)}</span>
                                  </div>
                                )}
                                
                                {company.website && (
                                  <div className="flex items-center text-gray-600 dark:text-gray-400">
                                    <Globe className="h-4 w-4 mr-1 flex-shrink-0" />
                                    <span className="truncate">{company.website}</span>
                                  </div>
                                )}
                                
                                {company.phone && (
                                  <div className="flex items-center text-gray-600 dark:text-gray-400">
                                    <Phone className="h-4 w-4 mr-1 flex-shrink-0" />
                                    <span>{company.phone}</span>
                                  </div>
                                )}
                                
                                {(company.city || company.state || company.country) && (
                                  <div className="flex items-center text-gray-600 dark:text-gray-400">
                                    <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
                                    <span className="truncate">
                                      {[company.city, company.state, company.country].filter(Boolean).join(', ')}
                                    </span>
                                  </div>
                                )}
                              </div>
                              
                              {company.description && (
                                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                                  {company.description}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 bg-gray-50 dark:bg-gray-800">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {selectedCompanies.size > 0 && (
                  <span>
                    {selectedCompanies.size} compan{selectedCompanies.size !== 1 ? 'ies' : 'y'} selected for import
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={onClose}
                  disabled={importMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={selectedCompanies.size === 0 || importMutation.isPending}
                  className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
                >
                  {importMutation.isPending ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Importing...
                    </>
                  ) : (
                    `Import ${selectedCompanies.size} Compan${selectedCompanies.size !== 1 ? 'ies' : 'y'}`
                  )}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Enforcement Modal */}
      {showEnforcementModal && (
        <EnforcementModal
          isOpen={showEnforcementModal}
          error={enforcementError}
          onClose={() => setShowEnforcementModal(false)}
        />
      )}
    </>
  );
};

export default HubSpotCompanyImportModal;


